<template>
    <v-container
        class="step-container"
        v-if="site && current"
    >
        <c-drop-message
            v-if     = "drag.notAllowed"
            :message ="drag.message"
        />

        <d-attach-confirm
            v-model = "attach.visible"
            :data   = "attach.data"
            @attach = "onAttach"
            @cancel = "onAttachCancel"
        />

        <drop @drop="onDrop" mode="cut" :accepts-type="onAcceptType">
            <v-row class="no-frame">
                <v-col class="step-first-column" style="border: none; padding: 0; min-height: 680px">
                    <div
                        class="lm-border pa-3"
                        :style="index === 0 ? 'flex: 1' : ''" :class="mainBoxClass"
                    >
                        <!-- Status -->
                        <v-row class="no-frame mt-0" >
                            <v-col class="no-frame">
                                <inc-status
                                    v-model = "current.status"
                                    :items  = "step.status"
                                    :label  = "$t('invoice.status')"
                                    @change = "onStatusChanged"
                                />
                            </v-col>
                        </v-row>
                        <!-- Status -->

                        <!-- Processing time -->
                        <inc-processing-interval
                            v-if    = "index === 0"
                            :data   = "data"
                            :status = "current.status"
                            :background = "collective ? 'field-group-background-blue' : ''"
                        />
                        <!-- Processing time -->

                        <!-- Order - main invoice -->
                        <v-row class="no-frame mt-0" >
                            <v-col class="no-frame mr-2">
                                <v-text-field
                                    :label            = "$t('invoice.ordernumber')"
                                    v-model           = "data.ordernumber"
                                    :background-color = "fileEntryFieldBackground"
                                    autocomplete      = "off"
                                    autocorrect       = "off"
                                    autocapitalize    = "off"
                                    spellcheck        = "false"
                                    @input            = "onOrdernumber"
                                />
                            </v-col>
                            <v-col class="no-frame ml-2">
                                <f-enum-select
                                    v-if        = "hasTag('vandalism')"
                                    v-model     = "data.vandalism"
                                    :label      = "$t('invoice.forwardings')"
                                    :readonly   = "readonly"
                                    :background = "fileEntryFieldBackground"
                                    module      = "inc"
                                    type        = "forwardings"
                                />
                            </v-col>
                        </v-row>
                        <!-- Order - main invoice -->


                        <!-- Description -->
                        <v-row
                            v-if="hasTag('description')"
                            class="no-frame mt-5"
                        >
                            <v-textarea
                                no-resize
                                label          = "Beschreibung"
                                v-model        = "data.description"
                                :background-color = "fileEntryFieldBackground"
                                rows           = "8"
                                autocomplete   = "off"
                                autocorrect    = "off"
                                autocapitalize = "off"
                                spellcheck     = "false"
                            />
                        </v-row>
                        <!-- Description -->

                        <v-row
                            v-if  = "index === 0 && complainReasonAllowed"
                            class = "no-frame mt-4"
                            style="display: block;flex:1;"
                        >
                            <v-textarea
                                v-model        = "data.complaintreason"
                                :label         = "$t('invoice.complaintreason')"
                                :background-color = "fileEntryFieldBackground"
                                :rows          = "hasTag('assessmentselect') ? 3 : 8"
                                autocomplete   = "off"
                                autocorrect    = "off"
                                autocapitalize = "off"
                                spellcheck     = "false"
                                class="pt-0"
                                hide-details
                                no-resize
                            />
                        </v-row >
                        <v-row  class = "no-frame mt-4" v-if  = "index === 0">
                            <inc-manufacturer
                                v-model     = "data.manufacturer"
                                :rules      = "[requiredOnTerminationRule]"
                                :background = "fileEntryFieldBackground"
                                @changed    = "onManufacturer"
                            />
                        </v-row>
                    </div>

                    <!-- Abstract of issue step data -->
                    <v-row
                        class="no-frame mt-3 mr-2"
                        v-if="hasTag('issue')"
                        style="flex:1;"
                    >
                        <v-col class="no-frame">
                            <inc-issue-abstract
                                :incident   = "incident"
                                :data       = "previous"
                                :current    = "previousCurrent"
                                :unit       = "unit"
                                :collective = "collective"
                                style="height: 512px"
                            />
                        </v-col>
                    </v-row>
                    <!-- Abstract of issue step data -->

                    <!-- Abstract of bid data -->
                    <v-row
                        class="no-frame mt-3 mr-2"
                        v-if="isRepair && index !== 0"
                    >
                        <v-col class="no-frame">
                            <v-col class="no-frame">
                                <inc-bid-abstract
                                    :incident   = "incident"
                                    :data       = "data"
                                    :unit       = "unit"
                                    :form       = "form"
                                    :collective = "collective"
                                    style       ="height: 512px"
                                />
                            </v-col>
                        </v-col>
                    </v-row>
                    <!-- Abstract of bid data -->

                    <!-- Abstract of assessment data -->
                    <v-row
                        class="no-frame mr-2 mt-2"
                        v-if="hasTag('assessment')"
                    >
                        <v-col class="no-frame">
                            <inc-assessment-abstract
                                :assessment = "previousCurrent"
                                :data       = "data"
                            />
                        </v-col>
                    </v-row>
                    <!-- Abstract of assessment data -->

                    <!-- assessment selection -->
                    <v-row
                        class="no-frame mr-2"
                        v-if="hasTag('assessmentselect')"
                    >
                        <v-col class="no-frame">
                            <v-hover>
                                <template v-slot:default="{ hover }">
                                    <inc-assessment-selection
                                        :class    = "`elevation-${ hover ? 12 : 0}`"
                                        :invoice  = "current"
                                        :height   = "current.status.id === 31 ? 344 : 424"
                                        @selected = "onAssessment"
                                    />
                                    </template>
                            </v-hover>
                        </v-col>
                    </v-row>
                    <!-- assessment selection -->
                </v-col>

                <v-col class="step-middle-column" style="border: none; padding: 0; overflow-y: auto; width: calc(100% + 8px)">
                    <!-- Abstract of maintenance contract data -->
                    <c-contract-mnt-abstract
                        v-if       = "incident.typeId === type.maintenance"
                        class      = "lm-border pa-3 mt-0 mb-3"
                        :show-unit = "this.data.collection.length > 1"
                        style      = "height: 200px;"
                    />
                    <!-- Abstract of maintenance contract data -->

                    <!-- Abstract of emergency contract data -->
                    <c-contract-esc-abstract
                        v-if        = "incident.typeId === type.emergency || incident.typeId === type.guardcost"
                        :collective = "collective"
                        class       = "lm-border pa-3 mb-3"
                    />
                    <!-- Abstract of emergency contract data -->

                    <p-invoice-sum
                        v-if         = "collective && this.data.collection.length > 1"
                        :invoice     = "data"
                        :status      = "current.status"
                        :show-costs  = "index !== 0 && incident.typeId !== 1 && !bid"
                        :calculation = "collectiveCalculation"
                        :collective  = "collective"
                        :validate    = "incident.typeId !== type.maintenance && incident.typeId !== type.emergency && incident.typeId !== type.assessmentcost && incident.typeId !== type.assessmentinvoice"
                        @stampmail   = "onSendStampMail"
                        @stamped     = "onStamped"
                        class        = "mb-3"
                        ref          = "invoicesum"
                    />

                    <p-inc-invoice-data
                        v-if         = "calculation && hasTag('assessmentselect')"
                        :show-costs  = "index !== 0 && incident.typeId !== 1 && !bid"
                        :form        = "form"
                        :data        = "current"
                        :bid         = "bid"
                        :unit        = "unit"
                        :collective  = "collective"
                        :calculation = "unitCalculation"
                        :validate    = "incident.typeId !== type.emergency && incident.typeId !== type.assessmentcost"
                        @stampmail   = "onSendStampMail"
                        @stamped     = "onStamped"
                        @reset       = "onReset"
                        class        = "mt-0"
                        :style="index === 0 || !complainReasonAllowed ? 'flex: 1' : ''"
                    >
                        <template v-slot:required>
                            <div v-if="hasTag('assessmentselect')" >
                                <f-read-only-field
                                    class="w-50 mr-2"
                                    color="pt-0-5 pl-1 ro-background"
                                    label="Rechnungsbetrag laut Vertrag"
                                    :align-right="true"
                                    dense
                                    :value = "requiredPrice()"
                                />
                            </div>
                        </template>
                    </p-inc-invoice-data>

                    <p-inc-invoice-data
                        v-if         = "calculation && !hasTag('assessmentselect')"
                        :show-costs  = "index !== 0 && incident.typeId !== 1 && !bid"
                        :form        = "form"
                        :data        = "current"
                        :bid         = "bid"
                        :unit        = "unit"
                        :collective  = "collective && this.data.collection.length > 1"
                        :calculation = "unitCalculation"
                        :validate    = "incident.typeId !== type.emergency && incident.typeId !== type.assessmentcost"
                        @stampmail   = "onSendStampMail"
                        @stamped     = "onStamped"
                        @reset       = "onReset"
                        class        = "mt-0"
                        :style="index === 0 || !complainReasonAllowed ? 'flex: 1' : ''"
                    />

                    <v-row
                        v-if  = "index > 0 && complainReasonAllowed"
                        class = "no-frame lm-border pa-3 mt-4"
                        style="display: block;flex:1;"
                    >
                        <f-title :title="$t('invoice.complaintreason')" />

                        <v-textarea
                            v-model        = "data.complaintreason"
                            rows           = "5"
                            autocomplete   = "off"
                            autocorrect    = "off"
                            autocapitalize = "off"
                            spellcheck     = "false"
                            class="pt-0"
                            hide-details
                            no-resize
                        />
                    </v-row>
                </v-col>

                <v-col class="step-middle-column" style="max-width: calc(25% - 20px); border: none; padding: 0;">
                    <div
                        class = "lm-border pa-3 mb-3"
                        v-if  = "isGwl"
                    >
                        <f-title :title="$t('inc.edit.invoice.warranty')" class="mt-1"/>
                        <inc-warranty-edit
                            :site ="previousCurrent"
                        />
                    </div>

                    <p-inc-invoice-recipients
                        :usage      = "usage"
                        :separated  = "separated"
                        :recipients = "recipients"
                        :data       = "data"
                        :collective = "collective"
                        style       = "flex:1; overflow-y: scroll;"
                    />
                </v-col>

                <v-col class="step-last-column">
                    <crm-journal
                        :incident = "incident"
                        @front    ="onJournalFront"
                        style     = "margin-top: -10px"
                    />
                </v-col>
            </v-row>
        </drop>
    </v-container>
</template>

<script>

import { Drop }                from "vue-easy-dnd"
import Core                    from "@/modules/core/service/Core";
import Inc                     from "@/modules/inc/service/Inc.js";
import Bid                     from "@/modules/inc/service/Bid";
import Site                    from "@/modules/msd/unit/service/Site";
import FTitle                  from "@/modules/core/components/utils/FTitle.vue";
import CDropMessage            from "@/modules/inc/steps/components/common/CDropMessage.vue";
import FReadOnlyField          from "@/modules/core/components/utils/FReadOnlyField.vue";
import FEnumSelect             from "@/modules/core/components/utils/FEnumSelect.vue";
import IncStatus               from "@/modules/inc/steps/components/common/IncStatus.vue";
import IncWarrantyEdit         from "@/modules/inc/warranty/IncWarrantyEdit.vue";
import CrmJournal              from "@/modules/crm/components/main/CrmJournal.vue";
import IncAssessmentSelection  from "@/modules/inc/steps/components/assessment/IncAssessmentSelection.vue";
import CContractMntAbstract    from "@/modules/msd/company/contract/components/controls/mnt/CContractMntAbstract.vue";
import CContractEscAbstract    from "@/modules/msd/company/contract/components/controls/esc/CContractEscAbstract.vue";
import IncIssueAbstract        from "@/modules/inc/steps/components/issue/IncIssueAbstract.vue";
import IncBidAbstract          from "@/modules/inc/steps/components/repair/IncBidAbstract.vue";
import IncAssessmentAbstract   from "@/modules/inc/steps/components/assessment/IncAssessmentAbstract.vue";
import IncProcessingInterval   from "@/modules/inc/steps/components/common/IncProcessingInterval.vue";
import PIncInvoiceData         from "@/modules/inc/steps/components/invoice/private/PIncInvoiceData.vue";
import PIncInvoiceRecipients   from "@/modules/inc/steps/components/invoice/private/PIncInvoiceRecipients.vue";
import PInvoiceSum             from "@/modules/inc/steps/components/invoice/private/PInvoiceSum.vue";
import InvoiceMixin            from "@/modules/inc/steps/components/mixins/InvoiceMixin";
import StepMixin               from "@/modules/inc/steps/components/mixins/StepMixin";
import DAttachConfirm          from "@/modules/inc/steps/components/common/DAttachConfirm.vue";
import IncManufacturer         from "@/modules/inc/steps/components/common/IncManufacturer.vue";

export default {
    name: "IncInvoice",

    mixins: [
        InvoiceMixin,
        StepMixin
    ],

    components: {
        IncManufacturer,
        DAttachConfirm,
        FReadOnlyField,
        IncAssessmentSelection,
        CDropMessage,
        IncProcessingInterval,
        IncStatus,
        PInvoiceSum,
        FEnumSelect,
        IncWarrantyEdit,
        FTitle,
        PIncInvoiceRecipients,
        CContractEscAbstract,
        CContractMntAbstract,
        IncBidAbstract,
        PIncInvoiceData,
        IncAssessmentAbstract,
        IncIssueAbstract,
        CrmJournal,
        Drop,
    },

    props: {
        incident: {

        },

        data: {
            required: true,
            type    : Object,
            default : function () {
                return {
                    tags: null,
                    complaintreason: null,
                    costtype        : null,
                    benefitRecipient: null,
                    invoiceRecipient: null,
                    forwardRecipient: null,
                    sites: [
                        {
                            benefit: null,
                            invoice: null,
                            forward: null
                        }
                    ]
                }
            },
        },

        sites: {

        },
    },

    data: () => ({
        refresh: 0,
        type   : Inc.type,
        recipients: null,
        usage: null,
        separated: false,
        USAGE: Site.RECIPIENT.USAGE,
    }),

    created() {
        this.$bus.$on(this.$incident.events.context.entered, this.onContext);
        this.$bus.$on(this.$events.drag.end, this.onDragend);
        this.freeze = JSON.stringify(this.data);
        this.form = this.$parent;

        if (this.isRepair) {
            this.$root.$off("repair-sync");
            this.$root.$on("repair-sync", (sync) => {
                this.data[sync.name] = sync.value;
            });
        }
    },

    beforeDestroy() {
        this.$bus.$off(this.$incident.events.context.entered, this.onContext);
        this.$bus.$off(this.$events.drag.end, this.onDragend);
    },

    mounted() {
        this.form = this.$parent;
        this.$nextTick(() => {
            this.form.validate();
            this.initBase();
        });
    },

    computed: {
        preferences () {
            return this.$store.getters.preferences;
        },

        site () {
            return this.$store.getters.site;
        },

        zusContract () {
            if (!this.site["liftmanagement"]) {
                return null;
            }

            return this.site["liftmanagement"]["zusContract"];
        },

        contracts () {
            return this.$store.getters.site["liftmanagement"];
        },

        requiredRule() {
            return Core.rule.required;
        },

        previous () {
            if (this.index === 0) {
                return null;
            }

            return this.incident.data[this.index-1];
        },

        ordernumber () {
            if (this.index > 0) {
                const prev = this.incident.data[this.index - 1];
                if (prev.ordernumber) {
                    return prev.ordernumber;
                }
            }

            return null;
        },

        /**
         * In case of a repair invoice return the favorite bid
         *
         * @returns case of a repair invoice return the favorite bid, otherwise null.
         */
        bid () {
            if (this.index === 0 || !this.incident.data[this.index-1].bids) {
                return null;
            }

            return this.incident.data[this.index-1].bids[0];
        },

        previousCurrent () {
            if (!this.unit || !this.previous) {
                return null;
            }

            return this.previous.collection.find (s => s.unit.id === this.unit.id);
        },

        fileEntryFieldBackground () {
            return Inc.style.fileEntryFieldBackground(this.data);
        },

        isRepair () {
            if (this.index === 0) {
                return false;
            }

            return this.previous.bids !== undefined;
        },

        isGwl () {
            return this.incident.typeId !== this.type.falsealarm
                && this.incident.typeId !== this.type.maintenance
                && this.incident.typeId !== this.type.emergency
                && this.incident.typeId !== this.type.assessmentcost
                && this.incident.typeId !== this.type.guardcost
                && this.incident.typeId !== this.type.assessmentinvoice
                && this.data.stepId     !== 51
            ;
        },

        mainBoxClass () {
            if (this.hasTag('assessmentselect')) {
                if (this.current.status.id === 31) {
                    return 'assessment-invoice-complain'
                }

                return 'assessment-invoice';
            }

            return 'default-invoice'
        }
    },

    watch: {
        current: {
            deep: true,
            handler: function () {
                if (!this.current || !this.$refs.invoicesum) {
                    return;
                }

                this.$refs.invoicesum.calculate();
            }
        },

        data () {
        //    this.loadRecipients();
        },

        site () {
            if (this.site && this.unit && (this.site.id !== this.unit.id || !this.recipients)) {
                this.loadRecipients();
            }
        }
    },

    methods: {
        /**
         * Status of the processing step has changed. Store the incident.
         */
        onStatusChanged (status) {
            const units = Inc.step.units(this.data);
            this.prevStatus = status;
            if (units.length > 1) {
                const parameter = [ status.name, this.unit.fabricNo ];
                Core.message.confirm(this, this.$t('invoice.update.status.query'), this.$t('invoice.update.status.text', parameter), Core.message.Default, (value => {
                    if (value) {
                        this.data.collection.forEach(invoice => {
                            invoice.status = status;
                        });
                    }
                }))
            }

            this.current.status = status;
            this.validate().then (() => {
                this.$emit("save");
            });
        },

        _validate () {
            this.form.validate();
        },

        /**
         * Sets the given unit als new context for the processing step. T
         *
         * @param unit the new unit context.
         */
        _setContext (unit) {
            this.unit = unit;
            this.$nextTick(() => {
                this.loadRecipients();
            })
        },

        /**
         * Checks if the current processing step is terminated.
         * @returns true, if the processing step is terminated, false otherwise.
         */
        _isTerminated () {
            return this.current.status.type !== 'T';
        },

        _status () {
            if (!this.current) {
                return null;
            }

            return this.current.status;
        },

        /**
         * Force an update of relevat componets. E.g. when the list of assigned sites changes.
         */
        _forceUpdate () {
            this.freeze = JSON.stringify(this.data);
        },

        _unitStepStatus () {
            if (!this.data.visible) {
                return null;
            }

            if (!this.previousCurrent) {
                return this.form.value ? 'success' : 'error';
            }

            if (this.previousCurrent.status.type !== 'T') {
                return null;
            }

            return this.form.value ? 'success' : 'error';
        },

        /**
         * Validate the processing step and inform the main dialog about a possible change.
         */
        validate () {
            const r = this.form.validate();
            if (this.current)
            this.current.valid = r;
            return Promise.resolve();
        },

        onContext () {
            this.$nextTick().then(() => {
                const prev = this.previous;
                if (prev && this.collection) {
                    this.collective = prev.collective && this.collection.length > 1;
                    return;
                }

                this.collective = this.incident.sites.length > 1;
            })
        },

        display () {
            if (this.index > 0 && !this.data.beginDate) {
                this.data.beginDate = this.incident.data[this.index-1].endDate;
            }
        },

        onReset () {
            this.save();
        },

        onManufacturer () {

        },

        onOrdernumber () {
            this.$emit("sync", {
                index: this.index - 1,
                name : "ordernumber",
                value: this.data.ordernumber
            } );
        },

        onAssessment () {
            console.log("on assessment save")
            this.$emit("save", () => {
                this.$bus.$emit( this.$events.refreshdocuments);
            });
        },

        collectiveCalculation () {
            return this.calculation(true)
        },

        unitCalculation () {
            return this.calculation(false);
        },

        calculation (collective) {
            if (this.bid) {
                return Bid.price(collective, this.unit, this.bid);
            }

            switch (this.incident.typeId) {
                case Inc.type.assessment:
                    return this.previousCurrent ? Site.contract.zus.costs(this.site, this.previousCurrent) : null;
                case Inc.type.assessmentinvoice:
                    return this.current ? Site.contract.zus.costs(this.site, this.current.reference) : null;
                case Inc.type.maintenance:
                    return Site.contract.mnt.costs(this.site);
            }

            return function () {
                return null;
            };
        },

        requiredPrice () {
            if (!this.zusContract || !this.current || !this.current.reference) {
                return "---";
            }

            return this.toMoney(Site.contract.zus.costs(this.site, this.current.reference));
        },

        requiredOnTerminationRule (value)  {
            return Core.rule.requiredOnTermination(value, this.current.status);
        },

        save () {
            this.$emit("save");
        },

        loadRecipients () {
            if (!this.site) {
                return;
            }

            let unitIds = this.incident.sites.map(s => s.id);
            if (!unitIds) {
                return;
            }
            let usage = this.USAGE.INCIDENT;

            if (this.index === 0) {
                usage = this.USAGE.COSTS;
            } else if (this.isRepair) {
                usage = this.USAGE.REPAIR;
            }

            this.usage = usage;
            this.separated = this.site.customer.recipientsSeparated;

            Site.recipients(unitIds, usage,(data) => {
                this.recipients = data;
                this.recipients.invoice = this.prepareRecipientList(this.recipients.invoice, this.data.invoiceRecipient);
                this.recipients.benefit = this.prepareRecipientList(this.recipients.benefit, this.data.benefitRecipient);
                this.recipients.forward = this.prepareRecipientList(this.recipients.forward, this.data.forwardRecipient);
            });
        },

        prepareRecipientList (list, recipient) {
            if (!list || list.length === 0) {
                const result = [];
                if (recipient) {
                    Site.recipient(recipient.id, (data) => {
                        result.push (data);
                    });
                }

                return result;
            }

            if (recipient) {
                const index = list.findIndex(r => r.id === recipient.id);
                if (index < 0) {
                    Site.recipient(recipient.id, (data) => {
                        list.push (data);
                    });
                }
            }

            return list;
        },

        toMoney (number) {
            return Core.format.money (number);
        },

        hasTag (tag) {
            return this.data.tags.includes(tag)
        },
    }
}
</script>

<style scoped>

::v-deep textarea::-webkit-scrollbar {
    width: 8px !important;
}

::v-deep textarea::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 0 rgba(0, 0, 0, 0.3)!important;
    border-radius: 10px;
}

::v-deep textarea::-webkit-scrollbar-thumb {
    background: white;
}

::v-deep textarea:hover::-webkit-scrollbar-thumb {
    background: lightgray;
    border-radius: 10px;
}

.assessment-invoice {
    min-height: 254px;
}

.assessment-invoice-complain {
    min-height: 334px
}

.default-invoice {
    max-height: 690px;
}

::v-deep .v-label--active {
    max-width: 133%;
    transform: translateY(-22px) scale(.75);
    pointer-events: auto;
}

</style>
